import { useMemo } from "react";

const STORAGE_KEY = "admin_airbnb_links";

interface StoredLink {
  id: number;
  name: string;
  area: string;
  url: string;
  status: "linked" | "pending";
  isCustom?: boolean;
}

/**
 * 어드민에서 저장한 에어비앤비 URL을 우선으로 반환.
 * 어드민에 저장된 URL이 없으면 mock 기본값 사용.
 */
export function useAirbnbUrl(accommodationId: number, fallbackUrl: string): string {
  return useMemo(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const links: StoredLink[] = JSON.parse(saved);
        const found = links.find((l) => l.id === accommodationId);
        if (found && found.url && found.url.trim()) {
          return found.url.trim();
        }
      }
    } catch {
      // ignore parse errors
    }
    return fallbackUrl;
  }, [accommodationId, fallbackUrl]);
}

/**
 * 전체 숙소 airbnb URL 맵 반환 (id -> url)
 */
export function useAirbnbUrlMap(): Record<number, string> {
  return useMemo(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const links: StoredLink[] = JSON.parse(saved);
        const map: Record<number, string> = {};
        links.forEach((l) => {
          if (l.url && l.url.trim()) {
            map[l.id] = l.url.trim();
          }
        });
        return map;
      }
    } catch {
      // ignore
    }
    return {};
  }, []);
}

/**
 * URL이 에어비앤비 실제 숙소 페이지인지 확인
 * (검색 페이지 /s/ URL이면 false)
 */
export function isRealAirbnbUrl(url: string): boolean {
  if (!url) return false;
  // 실제 숙소: /rooms/숫자 형태
  return /airbnb\.(co\.kr|com)\/rooms\/\d+/.test(url);
}
